import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEnhancedGameSession } from '../hooks/useEnhancedGameSession'
import { VoiceChat } from '../components/VoiceChat'
import { sounds } from '../utils/sounds'
import { UserProfileManager } from '../utils/userProfile'
import { GameRoomSidebar, GameRoomHeader } from '../components/GameRoom'
import type { Message, HistoryItem } from '../components/GameRoom'
import { useSidebar } from '../hooks/useSidebar'
import '../styles/Codenames.css'
import '../styles/UniversalGameRoom.css'
import '../styles/mobile-fixes.css'
import '../styles/mobile-bottom-sheet.css'

type GameMode = 'select' | 'local' | 'network'
type GamePhase = 'select' | 'setup' | 'lobby' | 'playing' | 'gameOver'
type Team = 'red' | 'blue'
type Role = 'spymaster' | 'operative'
type CardType = 'red' | 'blue' | 'neutral' | 'assassin' | 'revealed'

interface Card {
  word: string
  type: CardType
  revealed: boolean
}

interface PlayerInfo {
  name: string
  team: Team | null
  role: Role | null
}

// Word bank for the game
const WORDS = [
  'AFRICA', 'AGENT', 'AIR', 'ALIEN', 'ALPS', 'AMAZON', 'AMBULANCE', 'AMERICA', 'ANGEL', 'ANTARCTICA',
  'APPLE', 'ARM', 'ATLANTIS', 'AUSTRALIA', 'AZTEC', 'BACK', 'BALL', 'BAND', 'BANK', 'BAR',
  'BARK', 'BAT', 'BATTERY', 'BEACH', 'BEAR', 'BEAT', 'BED', 'BEIJING', 'BELL', 'BELT',
  'BERLIN', 'BERMUDA', 'BERRY', 'BILL', 'BLOCK', 'BOARD', 'BOLT', 'BOMB', 'BOND', 'BOOM',
  'BOOT', 'BOTTLE', 'BOW', 'BOX', 'BRIDGE', 'BRUSH', 'BUCK', 'BUFFALO', 'BUG', 'BUGLE',
  'BUTTON', 'CALF', 'CANADA', 'CAP', 'CAPITAL', 'CAR', 'CARD', 'CARROT', 'CASINO', 'CAST',
  'CAT', 'CELL', 'CENTAUR', 'CENTER', 'CHAIR', 'CHANGE', 'CHARGE', 'CHECK', 'CHEST', 'CHICK',
  'CHINA', 'CHOCOLATE', 'CHURCH', 'CIRCLE', 'CLIFF', 'CLOAK', 'CLUB', 'CODE', 'COLD', 'COMIC',
  'COMPOUND', 'CONCERT', 'CONDUCTOR', 'CONTRACT', 'COOK', 'COPPER', 'COTTON', 'COURT', 'COVER', 'CRANE',
  'CRASH', 'CRICKET', 'CROSS', 'CROWN', 'CYCLE', 'CZECH', 'DANCE', 'DATE', 'DAY', 'DEATH',
  'DECK', 'DEGREE', 'DIAMOND', 'DICE', 'DINOSAUR', 'DISEASE', 'DOCTOR', 'DOG', 'DRAFT', 'DRAGON',
  'DRESS', 'DRILL', 'DROP', 'DUCK', 'DWARF', 'EAGLE', 'EGYPT', 'EMBASSY', 'ENGINE', 'ENGLAND',
  'EUROPE', 'EYE', 'FACE', 'FAIR', 'FALL', 'FAN', 'FENCE', 'FIELD', 'FIGHTER', 'FIGURE',
  'FILE', 'FILM', 'FIRE', 'FISH', 'FLUTE', 'FLY', 'FOOT', 'FORCE', 'FOREST', 'FORK',
  'FRANCE', 'GAME', 'GAS', 'GENIUS', 'GERMANY', 'GHOST', 'GIANT', 'GLASS', 'GLOVE', 'GOLD',
  'GRACE', 'GRASS', 'GREECE', 'GREEN', 'GROUND', 'HAM', 'HAND', 'HAWK', 'HEAD', 'HEART',
  'HELICOPTER', 'HIMALAYAS', 'HOLE', 'HOLLYWOOD', 'HONEY', 'HOOD', 'HOOK', 'HORN', 'HORSE', 'HORSESHOE',
  'HOSPITAL', 'HOTEL', 'ICE', 'ICE CREAM', 'INDIA', 'IRON', 'IVORY', 'JACK', 'JAM', 'JET',
  'JUPITER', 'KANGAROO', 'KETCHUP', 'KEY', 'KID', 'KING', 'KIWI', 'KNIFE', 'KNIGHT', 'LAB',
  'LAP', 'LASER', 'LAWYER', 'LEAD', 'LEMON', 'LEPRECHAUN', 'LIFE', 'LIGHT', 'LIMOUSINE', 'LINE',
  'LINK', 'LION', 'LITTER', 'LOCH NESS', 'LOCK', 'LOG', 'LONDON', 'LUCK', 'MAIL', 'MAMMOTH',
  'MAPLE', 'MARBLE', 'MARCH', 'MASS', 'MATCH', 'MERCURY', 'MEXICO', 'MICROSCOPE', 'MILLIONAIRE', 'MINE',
  'MINT', 'MISSILE', 'MODEL', 'MOLE', 'MOON', 'MOSCOW', 'MOUNT', 'MOUSE', 'MOUTH', 'MUG',
  'NAIL', 'NEEDLE', 'NET', 'NEW YORK', 'NIGHT', 'NINJA', 'NOTE', 'NOVEL', 'NURSE', 'NUT',
  'OCTOPUS', 'OIL', 'OLIVE', 'OLYMPUS', 'OPERA', 'ORANGE', 'ORGAN', 'PALM', 'PAN', 'PANTS',
  'PAPER', 'PARACHUTE', 'PARK', 'PART', 'PASS', 'PASTE', 'PENGUIN', 'PHOENIX', 'PIANO', 'PIE',
  'PILOT', 'PIN', 'PIPE', 'PIRATE', 'PISTOL', 'PIT', 'PITCH', 'PLANE', 'PLASTIC', 'PLATE',
  'PLATYPUS', 'PLAY', 'PLOT', 'POINT', 'POISON', 'POLE', 'POLICE', 'POOL', 'PORT', 'POST',
  'POUND', 'PRESS', 'PRINCESS', 'PUMPKIN', 'PUPIL', 'PYRAMID', 'QUEEN', 'RABBIT', 'RACKET', 'RAY',
  'REVOLUTION', 'RING', 'ROBIN', 'ROBOT', 'ROCK', 'ROME', 'ROOT', 'ROSE', 'ROULETTE', 'ROUND',
  'ROW', 'RULER', 'SATELLITE', 'SATURN', 'SCALE', 'SCHOOL', 'SCIENTIST', 'SCORPION', 'SCREEN', 'SCUBA DIVER',
  'SEAL', 'SERVER', 'SHADOW', 'SHAKESPEARE', 'SHARK', 'SHIP', 'SHOE', 'SHOP', 'SHOT', 'SINK',
  'SKYSCRAPER', 'SLIP', 'SLUG', 'SMUGGLER', 'SNOW', 'SNOWMAN', 'SOCK', 'SOLDIER', 'SOUL', 'SOUND',
  'SPACE', 'SPELL', 'SPIDER', 'SPIKE', 'SPINE', 'SPOT', 'SPRING', 'SPY', 'SQUARE', 'STADIUM',
  'STAFF', 'STAR', 'STATE', 'STICK', 'STOCK', 'STRAW', 'STREAM', 'STRIKE', 'STRING', 'SUB',
  'SUIT', 'SUPERHERO', 'SWING', 'SWITCH', 'TABLE', 'TABLET', 'TAG', 'TAIL', 'TAP', 'TEACHER',
  'TELESCOPE', 'TEMPLE', 'THEATER', 'THIEF', 'THUMB', 'TICK', 'TIE', 'TIME', 'TOKYO', 'TOOTH',
  'TORCH', 'TOWER', 'TRACK', 'TRAIN', 'TRIANGLE', 'TRIP', 'TRUNK', 'TUBE', 'TURKEY', 'UNDERTAKER',
  'UNICORN', 'VACUUM', 'VAN', 'VET', 'WAKE', 'WALL', 'WAR', 'WASHER', 'WASHINGTON', 'WATCH',
  'WATER', 'WAVE', 'WEB', 'WELL', 'WHALE', 'WHIP', 'WIND', 'WITCH', 'WORM', 'YARD'
]

export default function Codenames() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const autoJoinCode = searchParams.get('join')

  // Auto-fill from profile
  const userProfile = UserProfileManager.get()

  // Game mode and phase
  const [gameMode, setGameMode] = useState<GameMode>('select')
  const [phase, setPhase] = useState<GamePhase>('select')

  // Player info for network mode
  const [playerName, setPlayerName] = useState(userProfile?.name || '')
  const [playerNumber, setPlayerNumber] = useState<1 | 2>(1)
  const [myPlayerId, setMyPlayerId] = useState<number>(1) // Track which player slot (1-8) this user occupies

  // Network multiplayer
  const enhancedSession = useEnhancedGameSession({
    sessionCode: undefined,
    playerNumber,
    playerName,
  })

  const {
    session,
    createSession,
    joinSession,
    voice,
    sendMove,
    onMessage,
  } = enhancedSession

  // Universal sidebar state
  const [chatMessages, setChatMessages] = useState<Message[]>([])
  const [historyItems] = useState<HistoryItem[]>([])
  const sidebar = useSidebar()

  // Player info for all modes
  const [players, setPlayers] = useState<Map<number, PlayerInfo>>(new Map())

  // Game state
  const [cards, setCards] = useState<Card[]>([])
  const [currentTeam, setCurrentTeam] = useState<Team>('red')
  const [selectedTeam, setSelectedTeam] = useState<Team>('red')
  const [selectedRole, setSelectedRole] = useState<Role>('operative')
  const [redScore, setRedScore] = useState(0)
  const [blueScore, setBlueScore] = useState(0)
  const [redTarget, setRedTarget] = useState(9)
  const [blueTarget, setBlueTarget] = useState(8)
  const [winner, setWinner] = useState<Team | null>(null)

  // Initialize game (host only in network mode)
  const startGame = () => {
    // In network mode, only host can start
    if (gameMode === 'network' && playerNumber !== 1) return

    // Randomly select 25 words
    const shuffled = [...WORDS].sort(() => Math.random() - 0.5)
    const selectedWords = shuffled.slice(0, 25)

    // Randomly assign card types
    // 9 red, 8 blue, 7 neutral, 1 assassin (red starts first so gets 9)
    const types: CardType[] = [
      ...Array(9).fill('red'),
      ...Array(8).fill('blue'),
      ...Array(7).fill('neutral'),
      'assassin',
    ].sort(() => Math.random() - 0.5) as CardType[]

    const newCards: Card[] = selectedWords.map((word, idx) => ({
      word,
      type: types[idx],
      revealed: false,
    }))

    setCards(newCards)
    setCurrentTeam('red')
    setRedScore(0)
    setBlueScore(0)
    setRedTarget(9)
    setBlueTarget(8)
    setWinner(null)
    setPhase('playing')

    // Send initial state for network mode
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        cards: newCards,
        currentTeam: 'red',
        redScore: 0,
        blueScore: 0,
        redTarget: 9,
        blueTarget: 8,
        winner: null,
        phase: 'playing',
        players: Array.from(players.entries()).map(([id, info]) => ({ id, ...info })),
      }})
    }
  }

  // Handle card click
  const handleCardClick = (index: number) => {
    if (selectedRole === 'spymaster') return // Spymasters don't click cards
    if (cards[index].revealed) return
    if (winner) return

    // In network mode, only operatives of current team can click
    if (gameMode === 'network' && selectedTeam !== currentTeam) return

    const card = cards[index]
    const newCards = [...cards]
    newCards[index] = { ...card, revealed: true }
    setCards(newCards)

    // Update scores
    let newRedScore = redScore
    let newBlueScore = blueScore

    if (card.type === 'red') newRedScore++
    if (card.type === 'blue') newBlueScore++

    setRedScore(newRedScore)
    setBlueScore(newBlueScore)

    // Check for assassin (instant loss)
    let newWinner: Team | null = null
    if (card.type === 'assassin') {
      const losingTeam = currentTeam
      const winningTeam: Team = losingTeam === 'red' ? 'blue' : 'red'
      setWinner(winningTeam)
      newWinner = winningTeam
    }

    // Check for win conditions
    if (newRedScore === redTarget) {
      setWinner('red')
      newWinner = 'red'
    }
    if (newBlueScore === blueTarget) {
      setWinner('blue')
      newWinner = 'blue'
    }

    // If wrong card clicked, switch teams
    const newCurrentTeam = card.type !== currentTeam ? (currentTeam === 'red' ? 'blue' : 'red') : currentTeam
    if (!newWinner) {
      setCurrentTeam(newCurrentTeam)
    }

    // Send state update for network mode
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        cards: newCards,
        currentTeam: newCurrentTeam,
        redScore: newRedScore,
        blueScore: newBlueScore,
        redTarget,
        blueTarget,
        winner: newWinner,
        phase: newWinner ? 'gameOver' : 'playing',
      }})
    }
  }

  // End turn manually
  const endTurn = () => {
    const newCurrentTeam: Team = currentTeam === 'red' ? 'blue' : 'red'
    setCurrentTeam(newCurrentTeam)

    // Send state update for network mode
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        cards,
        currentTeam: newCurrentTeam,
        redScore,
        blueScore,
        redTarget,
        blueTarget,
        winner,
        phase: 'playing',
      }})
    }
  }

  // Update player team/role in lobby
  const updatePlayerInfo = (team: Team, role: Role) => {
    setSelectedTeam(team)
    setSelectedRole(role)

    if (gameMode === 'network') {
      const updatedPlayers = new Map(players)
      updatedPlayers.set(myPlayerId, { name: playerName, team, role })
      setPlayers(updatedPlayers)

      // Broadcast player info update
      sendMove({ type: 'player_info', playerId: myPlayerId, team, role })
    }
  }

  // Handler for sidebar message sending (network mode)
  const handleSidebarMessage = useCallback(async (message: string) => {
    if (gameMode !== 'network') return

    // Send chat message via session
    await sendMove({ type: 'chat', message, playerNumber })

    // Add to local chat immediately (optimistic update)
    const newMessage: Message = {
      sender: 'you',
      content: message,
      timestamp: Date.now()
    }
    setChatMessages(prev => [...prev, newMessage])
  }, [gameMode, sendMove, playerNumber])

  // Get card class
  const getCardClass = (card: Card): string => {
    const classes = ['codenames-card']

    // Spymaster view
    if (selectedRole === 'spymaster') {
      if (!card.revealed) {
        classes.push(`card-${card.type}`)
      } else {
        classes.push('card-revealed')
      }
    } else {
      // Operative view
      if (card.revealed) {
        classes.push(`card-${card.type}`)
        classes.push('card-revealed')
      } else {
        classes.push('card-hidden')
      }
    }

    return classes.join(' ')
  }

  // Sync game state (network mode)
  useEffect(() => {
    if (gameMode !== 'network') return

    const unsubscribeGameState = onMessage('game_state', (data) => {
      const state = data.state
      if (state.cards) setCards(state.cards)
      if (state.currentTeam) setCurrentTeam(state.currentTeam)
      if (state.redScore !== undefined) setRedScore(state.redScore)
      if (state.blueScore !== undefined) setBlueScore(state.blueScore)
      if (state.redTarget !== undefined) setRedTarget(state.redTarget)
      if (state.blueTarget !== undefined) setBlueTarget(state.blueTarget)
      if (state.winner !== undefined) setWinner(state.winner)
      if (state.phase) setPhase(state.phase)
      if (state.players) {
        const playersMap = new Map()
        state.players.forEach((p: any) => {
          playersMap.set(p.id, { name: p.name, team: p.team, role: p.role })
        })
        setPlayers(playersMap)
      }
    })

    const unsubscribePlayerInfo = onMessage('player_info', (data) => {
      const updatedPlayers = new Map(players)
      const existingPlayer = updatedPlayers.get(data.playerId) || { name: `Player ${data.playerId}` }
      updatedPlayers.set(data.playerId, {
        ...existingPlayer,
        team: data.team,
        role: data.role,
      })
      setPlayers(updatedPlayers)
    })

    const unsubscribeChat = onMessage('chat', (data) => {
      // Add incoming chat message
      console.log('[Codenames] Chat message:', data.message)
      const newMessage: Message = {
        sender: data.playerNumber === playerNumber ? 'you' : 'opponent',
        content: data.message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    })

    return () => {
      unsubscribeGameState()
      unsubscribePlayerInfo()
      unsubscribeChat()
    }
  }, [gameMode, onMessage, playerNumber])

  // Auto-join if code provided
  useEffect(() => {
    if (autoJoinCode && phase === 'select') {
      setGameMode('network')
      setPhase('setup')
    }
  }, [autoJoinCode, phase])

  // Check if players joined (network mode)
  useEffect(() => {
    if (gameMode === 'network' && phase === 'lobby' && session?.player2_name) {
      sounds.playerJoined()
    }
  }, [session?.player2_name, phase, gameMode])

  // Mode selection screen
  if (phase === 'select') {
    return (
      <div className="codenames-container">
        <button onClick={() => navigate('/')} className="back-button">
          ← Back
        </button>

        <div className="codenames-setup">
          <div className="codenames-logo">🕵️</div>
          <h1 className="codenames-title">Codenames</h1>
          <p className="codenames-subtitle">
            Give one-word clues to help your team identify their agents
          </p>

          <div className="codenames-setup-card">
            <h2>Select Game Mode</h2>
            <div className="mode-buttons">
              <button
                className="mode-button mode-local"
                onClick={() => { setGameMode('local'); setPhase('setup'); }}
              >
                <div className="mode-icon">👥</div>
                <h3>Local Game</h3>
                <p>Play with friends on this device</p>
              </button>

              <button
                className="mode-button mode-network"
                onClick={() => { setGameMode('network'); setPhase('setup'); }}
              >
                <div className="mode-icon">🌐</div>
                <h3>Online</h3>
                <p>Play with anyone, anywhere (2 players)</p>
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Setup phase
  if (phase === 'setup') {
    // Network mode setup
    if (gameMode === 'network') {
      const handleCreateSession = async () => {
        if (!playerName.trim()) {
          alert('Please enter your name')
          return
        }
        UserProfileManager.save(playerName)
        const session = await createSession('codenames', playerName)
        if (session) {
          setPlayerNumber(1)
          setMyPlayerId(1)
          const newPlayers = new Map()
          newPlayers.set(1, { name: playerName, team: null, role: null })
          setPlayers(newPlayers)
          setPhase('lobby')
          sounds.success()
        }
      }

      const handleJoinSession = async () => {
        const code = autoJoinCode || prompt('Enter game code:')
        if (!code) return
        if (!playerName.trim()) {
          alert('Please enter your name')
          return
        }
        UserProfileManager.save(playerName)
        const success = await joinSession(code, playerName)
        if (success) {
          setPlayerNumber(2)
          setMyPlayerId(2)
          const newPlayers = new Map()
          newPlayers.set(2, { name: playerName, team: null, role: null })
          setPlayers(newPlayers)
          setPhase('lobby')
          sounds.success()
        }
      }

      return (
        <div className="codenames-container">
          <button onClick={() => setPhase('select')} className="back-button">
            ← Back
          </button>

          <div className="codenames-setup">
            <div className="codenames-logo">🕵️</div>
            <h1 className="codenames-title">Codenames</h1>
            <p className="codenames-subtitle">Online Multiplayer</p>

            <div className="codenames-setup-card">
              <div className="network-setup">
                <label>Your Name:</label>
                <input
                  type="text"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  placeholder="Enter your name"
                  className="name-input"
                />

                <div className="network-buttons">
                  <button onClick={handleCreateSession} className="create-button">
                    Create Game
                  </button>
                  <button onClick={handleJoinSession} className="join-button">
                    Join Game
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    // Local mode setup
    return (
      <div className="codenames-container">
        <button onClick={() => setPhase('select')} className="back-button">
          ← Back
        </button>

        <div className="codenames-setup">
          <div className="codenames-logo">🕵️</div>
          <h1 className="codenames-title">Codenames</h1>
          <p className="codenames-subtitle">
            Give one-word clues to help your team identify their agents
          </p>

          <div className="codenames-setup-card">
            <h2>How to Play</h2>
            <div className="rules-list">
              <div className="rule-item">
                <span className="rule-icon">👥</span>
                <span>Split into 2 teams: Red and Blue</span>
              </div>
              <div className="rule-item">
                <span className="rule-icon">🎭</span>
                <span>Each team has a Spymaster and Operatives</span>
              </div>
              <div className="rule-item">
                <span className="rule-icon">🗣️</span>
                <span>Spymaster gives one-word clue + a number (e.g., "Ocean, 2")</span>
              </div>
              <div className="rule-item">
                <span className="rule-icon">🤔</span>
                <span>Operatives guess which words match the clue</span>
              </div>
              <div className="rule-item">
                <span className="rule-icon">💀</span>
                <span>Avoid the Assassin (black card) - instant loss!</span>
              </div>
              <div className="rule-item">
                <span className="rule-icon">🏆</span>
                <span>First team to reveal all their agents wins!</span>
              </div>
            </div>

            <div className="role-selector">
              <div className="selector-group">
                <label>Your Team:</label>
                <div className="team-buttons">
                  <button
                    className={`team-btn red-btn ${selectedTeam === 'red' ? 'selected' : ''}`}
                    onClick={() => setSelectedTeam('red')}
                  >
                    🔴 Red Team
                  </button>
                  <button
                    className={`team-btn blue-btn ${selectedTeam === 'blue' ? 'selected' : ''}`}
                    onClick={() => setSelectedTeam('blue')}
                  >
                    🔵 Blue Team
                  </button>
                </div>
              </div>

              <div className="selector-group">
                <label>Your Role:</label>
                <div className="role-buttons">
                  <button
                    className={`role-btn ${selectedRole === 'spymaster' ? 'selected' : ''}`}
                    onClick={() => setSelectedRole('spymaster')}
                  >
                    🎭 Spymaster
                    <span className="role-desc">Give clues</span>
                  </button>
                  <button
                    className={`role-btn ${selectedRole === 'operative' ? 'selected' : ''}`}
                    onClick={() => setSelectedRole('operative')}
                  >
                    🕵️ Operative
                    <span className="role-desc">Make guesses</span>
                  </button>
                </div>
              </div>
            </div>

            <button onClick={startGame} className="start-button">
              Start Game
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Lobby phase (network mode)
  if (phase === 'lobby') {
    const shareUrl = `${window.location.origin}${window.location.pathname}?join=${session?.session_code}`
    const copyShareLink = () => {
      navigator.clipboard.writeText(shareUrl)
      alert('Link copied to clipboard!')
    }

    // Check if we have enough players and all have selected team/role
    const allPlayersReady = Array.from(players.values()).every(p => p.team !== null && p.role !== null)
    const minPlayers = players.size >= 2 // Need at least 2 players

    return (
      <div className="codenames-container">
        <button onClick={() => navigate('/')} className="back-button">
          ← Back
        </button>

        <div className="codenames-setup">
          <div className="codenames-logo">🕵️</div>
          <h1 className="codenames-title">Game Lobby</h1>

          <div className="codenames-setup-card">
            <div className="lobby-content">
              <div className="session-code">
                <label>Game Code:</label>
                <div className="code-display">{session?.session_code}</div>
                <button onClick={copyShareLink} className="copy-button">
                  Copy Share Link
                </button>
              </div>

              <div className="role-selector">
                <div className="selector-group">
                  <label>Your Team:</label>
                  <div className="team-buttons">
                    <button
                      className={`team-btn red-btn ${selectedTeam === 'red' ? 'selected' : ''}`}
                      onClick={() => updatePlayerInfo('red', selectedRole)}
                    >
                      🔴 Red Team
                    </button>
                    <button
                      className={`team-btn blue-btn ${selectedTeam === 'blue' ? 'selected' : ''}`}
                      onClick={() => updatePlayerInfo('blue', selectedRole)}
                    >
                      🔵 Blue Team
                    </button>
                  </div>
                </div>

                <div className="selector-group">
                  <label>Your Role:</label>
                  <div className="role-buttons">
                    <button
                      className={`role-btn ${selectedRole === 'spymaster' ? 'selected' : ''}`}
                      onClick={() => updatePlayerInfo(selectedTeam, 'spymaster')}
                    >
                      🎭 Spymaster
                      <span className="role-desc">Give clues</span>
                    </button>
                    <button
                      className={`role-btn ${selectedRole === 'operative' ? 'selected' : ''}`}
                      onClick={() => updatePlayerInfo(selectedTeam, 'operative')}
                    >
                      🕵️ Operative
                      <span className="role-desc">Make guesses</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="players-list">
                <h3>Players ({players.size}/2):</h3>
                {Array.from(players.entries()).map(([id, info]) => (
                  <div key={id} className="player-item">
                    <span className="player-status">{info.team && info.role ? '✓' : '⏳'}</span>
                    <span>{info.name}</span>
                    {info.team && info.role && (
                      <span className={`player-role ${info.team}`}>
                        {info.team === 'red' ? '🔴' : '🔵'} {info.role === 'spymaster' ? '🎭' : '🕵️'}
                      </span>
                    )}
                  </div>
                ))}
                {players.size < 2 && (
                  <div className="waiting-message">
                    Waiting for {2 - players.size} more player{2 - players.size !== 1 ? 's' : ''}...
                  </div>
                )}
              </div>

              <VoiceChat
                connectionStatus={voice.connectionStatus}
                voiceStatus={{
                  localMuted: voice.isMuted,
                  remoteMuted: false,
                  localConnected: voice.isConnected,
                  remoteConnected: voice.isConnected
                }}
                playerName={playerName}
                opponentName={playerNumber === 1 ? session?.player2_name : session?.player1_name}
                onConnect={voice.connect}
                onToggleMute={voice.toggleMute}
              />

              {allPlayersReady && minPlayers && playerNumber === 1 && (
                <button onClick={startGame} className="start-button">
                  Start Game
                </button>
              )}

              {allPlayersReady && minPlayers && playerNumber !== 1 && (
                <div className="waiting-message">
                  Waiting for host to start the game...
                </div>
              )}

              {(!allPlayersReady || !minPlayers) && (
                <div className="waiting-message">
                  {!minPlayers ? 'Need at least 2 players to start' : 'Waiting for all players to select team and role...'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Restart game
  const restartGame = () => {
    if (gameMode === 'network') {
      setPhase('lobby')
    } else {
      setPhase('setup')
    }
  }

  // Game Over phase
  if (phase === 'gameOver') {
    return (
      <div className="codenames-container">
        <div className="game-over-screen">
          <div className="gameover-icon">🎉</div>
          <h1>{winner === 'red' ? '🔴 Red Team' : '🔵 Blue Team'} Wins!</h1>
          <p className="gameover-message">
            {winner === 'red' ? 'Red team successfully identified all their agents!' : 'Blue team successfully identified all their agents!'}
          </p>

          <div className="final-stats">
            <div className="final-stat">
              <span className="stat-label">Red Team</span>
              <span className="stat-value">{redScore}/{redTarget}</span>
            </div>
            <div className="final-stat">
              <span className="stat-label">Blue Team</span>
              <span className="stat-value">{blueScore}/{blueTarget}</span>
            </div>
          </div>

          <div className="gameover-buttons">
            <button onClick={restartGame} className="restart-button">
              Play Again
            </button>
            <button onClick={() => navigate('/')} className="home-button">
              Back to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Playing phase
  if (phase === 'playing') {
    return (
      <div className="codenames-container universal-game-room">
        <GameRoomHeader
          gameTitle="Codenames"
          gameIcon="🕵️"
          mode={gameMode as 'local' | 'network'}
          players={gameMode === 'network'
            ? [
                { name: session?.player1_name || playerName, status: 'active' },
                { name: session?.player2_name || 'Opponent', status: session?.player2_name ? 'active' : 'disconnected' },
              ]
            : [
                { name: 'Red Team', status: currentTeam === 'red' ? 'active' : 'thinking' },
                { name: 'Blue Team', status: currentTeam === 'blue' ? 'active' : 'thinking' },
              ]
          }
          sessionCode={gameMode === 'network' ? session?.session_code : undefined}
          onBack={restartGame}
        />

        {winner && (
          <div className="winner-banner">
            🎉 {winner === 'red' ? '🔴 Red Team' : '🔵 Blue Team'} Wins! 🎉
          </div>
        )}

        <div className="game-room-content">
          <div className="game-board-area">
            <div className="game-info">
              <div className="team-badge-group">
                <div className={`team-badge red-badge ${currentTeam === 'red' ? 'active' : ''}`}>
                  <span className="badge-team">Red Team</span>
                  <span className="badge-score">{redScore}/{redTarget}</span>
                </div>
                <div className={`team-badge blue-badge ${currentTeam === 'blue' ? 'active' : ''}`}>
                  <span className="badge-team">Blue Team</span>
                  <span className="badge-score">{blueScore}/{blueTarget}</span>
                </div>
              </div>

              <div className="role-indicator">
                You are: <span className={`role-text ${selectedTeam}`}>
                  {selectedTeam === 'red' ? '🔴' : '🔵'} {selectedTeam.toUpperCase()} {selectedRole.toUpperCase()}
                </span>
              </div>

              {selectedRole === 'operative' && selectedTeam === currentTeam && !winner && (
                <button onClick={endTurn} className="end-turn-button">
                  End Turn
                </button>
              )}
            </div>

            {gameMode === 'network' && (
              <VoiceChat
                connectionStatus={voice.connectionStatus}
                voiceStatus={{
                  localMuted: voice.isMuted,
                  remoteMuted: false,
                  localConnected: voice.isConnected,
                  remoteConnected: voice.isConnected
                }}
                playerName={playerName}
                opponentName={playerNumber === 1 ? session?.player2_name : session?.player1_name}
                onConnect={voice.connect}
                onToggleMute={voice.toggleMute}
              />
            )}

            <div className="codenames-grid">
              {cards.map((card, index) => (
                <button
                  key={index}
                  className={getCardClass(card)}
                  onClick={() => handleCardClick(index)}
                  disabled={selectedRole === 'spymaster' || card.revealed || winner !== null}
                >
                  {card.word}
                </button>
              ))}
            </div>

            {selectedRole === 'spymaster' && (
              <div className="spymaster-legend">
                <div className="legend-title">🎭 Spymaster Key:</div>
                <div className="legend-items">
                  <span className="legend-item">🔴 Red ({redTarget - redScore} left)</span>
                  <span className="legend-item">🔵 Blue ({blueTarget - blueScore} left)</span>
                  <span className="legend-item">⬜ Neutral</span>
                  <span className="legend-item">💀 Assassin</span>
                </div>
              </div>
            )}
          </div>

          <GameRoomSidebar
            mode="network"
            gameState={{
              turn: `${currentTeam === 'red' ? '🔴 Red' : '🔵 Blue'} Team's Turn`,
              phase: winner ? 'Game Over' : 'Playing',
              score: {
                'Red': `${redScore}/${redTarget}`,
                'Blue': `${blueScore}/${blueTarget}`,
              }
            }}
            messages={chatMessages}
            historyItems={historyItems}
            onSendMessage={handleSidebarMessage}
            collapsed={sidebar.collapsed}
            onToggleCollapse={sidebar.toggle}
            position={sidebar.position}
            quickReactions={true}
          />
        </div>
      </div>
    )
  }

  return null
}
